// OIDC sign-in continuation page.
//
// A relying party's authorization request lands here after the backend
// /authorize endpoint stashed its parameters under a one-time request_id.
// This page wraps the EXISTING auth flow (no consent screen): it waits for the
// session, sends unauthenticated users through SignInPage with returnTo back
// here, then posts the Firebase ID token to /authorize/complete and forwards
// the browser to the relying party's redirect URL.
//
// An unverified email address does NOT divert anyone (#1832). The backend
// asserts the identity either way and reports `email_verified` truthfully as a
// claim, leaving the relying party to apply its own policy. This page must not
// pre-judge that, because it cannot: it holds an opaque request_id and never
// learns which client it is serving.
//
// Since #1904 it can also be asked for a password. When the relying party sent
// `max_age` or `prompt=login` and the session does not meet it, the backend
// answers OIDC_REAUTH_REQUIRED *without consuming the request*, and the form
// below re-authenticates in place. That is the one thing here that is new UI
// rather than a detour through an existing page, and the reasons are in the
// comment on `reauthenticate` below.
//
// Since #1924 it can also be asked WHICH account. `prompt=select_account`
// answers OIDC_ACCOUNT_SELECTION_REQUIRED, again without consuming, and the
// chooser below offers "continue as this account", "use a different account"
// and "cancel". Both refusals arrive the same way and for the same reason: the
// request survives so the user can answer it.
//
// Note which of those two questions the SERVER settles on its own. Somebody who
// authenticates during this request has already chosen an account, so the
// backend satisfies the selection from `auth_time` and no chooser is shown —
// which is why signing out to switch accounts terminates rather than looping.
// Only "I am staying as the account I am already signed in as" needs an answer
// from this page, and that is what `confirmed_sub` carries.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useAuth } from '@/hooks/useAuth';
import { auth } from '@/lib/firebase';
import { appConfig } from '@/lib/env';
import { reauthErrorMessage } from '@/lib/firebase-errors';
import { LoadingSpinner } from '@/components/ui';

type OidcContinuePhase = 'working' | 'select' | 'reauth' | 'error';

export function OidcContinuePage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // `firebaseUser`, NOT `user`. `user` is the Firestore profile document, and
  // `loadUserProfile` returns null only from its catch — a genuinely missing
  // document yields a synthesised profile instead. A transient Firestore read
  // failure therefore leaves `user` null and `isLoading` false while the
  // session is very much alive.
  //
  // Deciding on `user` sent a signed-in person to `<Navigate to="/">`, and
  // `PublicRoute` sends them straight back here again, because it exempts this
  // exact path from its diverts and navigates to `returnTo`. That is a redirect
  // loop with a live session, not a detour to sign in. `AuthContext` documents
  // the same hazard for `refreshUserProfile`; the `onAuthStateChanged` path is
  // unguarded. `OidcLogoutPage` reads the session for the same reason (#1901).
  const { firebaseUser, isLoading, signOut } = useAuth();
  const [phase, setPhase] = useState<OidcContinuePhase>('working');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /**
   * The relying party's display name, learned only from an
   * OIDC_ACCOUNT_SELECTION_REQUIRED refusal (#1924).
   *
   * Undefined is a real state, not a missing one: a client may carry no
   * registered name (#1925), and the chooser then asks about the account
   * without naming who is asking, exactly as `OidcLogoutPage` already does.
   */
  const [clientName, setClientName] = useState<string | undefined>(undefined);
  /** Shown above the chooser when it is being asked a second time. */
  const [selectNotice, setSelectNotice] = useState<string | null>(null);
  // Only the session-expired case offers a sign-in link: every other message
  // tells the user to return to the application they came from, and a link
  // into our own sign-in would be the wrong advice there.
  const [showSignIn, setShowSignIn] = useState(false);
  const [password, setPassword] = useState('');
  /** Guards all three answers this page can send: confirm the account,
   *  re-authenticate, and decline. One flag, because they are alternatives to
   *  each other and every one of them must disable the others' buttons. */
  const [actionPending, setActionPending] = useState(false);
  const [reauthError, setReauthError] = useState<string | null>(null);
  const startedRef = useRef(false);
  /** Whether a re-authentication has already been asked for on this page. */
  const challengedRef = useRef(false);
  /** Whether an account chooser has already been answered-and-refused once. */
  const chooserRepeatedRef = useRef(false);
  /** The subject the user confirmed at the chooser, if they got that far.
   *  Sent with a later decline so the server can name which question was
   *  refused; see `continueAsCurrentAccount`. */
  const confirmedSubRef = useRef<string | undefined>(undefined);

  const requestId = searchParams.get('request_id');

  const failSessionExpired = useCallback(() => {
    // Told to sign in again, and given a way to do it. Every other message
    // here says "go back to the application you came from", which the user can
    // act on; this one asks for something they cannot do from a page with no
    // links on it.
    setErrorMessage('Your session expired. Please sign in again.');
    setShowSignIn(true);
    setPhase('error');
  }, []);

  /**
   * Post the Firebase ID token to /authorize/complete and act on the answer.
   *
   * Called on mount, and AGAIN after a successful re-authentication or an
   * answered account chooser — which is why it is a callback rather than the
   * body of the effect. `startedRef` guards only the automatic first run; the
   * retries are deliberate and must not be suppressed by it.
   *
   * `confirmedSub` is sent only by the chooser's "continue as this account",
   * and names the subject the user was actually looking at (#1924). The server
   * refuses it unless it matches the uid the bearer token authenticates, so it
   * can confirm no account but this one — the point being that the session can
   * change between the chooser rendering and the button being pressed, since
   * Firebase persistence is shared across tabs on one origin. A bare "yes"
   * would confirm one account and mint for another.
   */
  const submitCompletion = useCallback(
    async (confirmedSub?: string) => {
      try {
        // Force-refresh rather than reuse whatever is cached. The backend
        // verifies with `checkRevoked: true`, and copies `auth_time` from this
        // token into the id_token, so that claim is only as current as the
        // token it came from. `email` and `email_verified` come from a live
        // Auth read instead (#1883), and fall back to this token's pair only
        // when that read does not complete (#2060).
        //
        // Load-bearing for the retry specifically: a re-authentication mints new
        // tokens, and without `true` here the second attempt would present the
        // cached pre-re-auth token, carrying the very `auth_time` that was just
        // refused. The user would type their password correctly and be asked
        // again, for ever.
        const idToken = await auth.currentUser?.getIdToken(true);
        if (!idToken) {
          failSessionExpired();
          return;
        }

        const response = await fetch(
          `${appConfig.api.baseUrl}/authorize/complete`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              request_id: requestId,
              ...(confirmedSub ? { confirmed_sub: confirmedSub } : {}),
            }),
          }
        );

        if (response.ok) {
          const body = (await response.json()) as { redirectTo: string };
          // Full-page navigation to the relying party's redirect_uri; this
          // is an external URL, not an in-app route.
          window.location.assign(body.redirectTo);
          return;
        }

        const body = (await response.json().catch(() => null)) as {
          error?: string;
          message?: string;
          errorRedirectTo?: string;
          clientName?: string;
        } | null;

        // Checked BEFORE `errorRedirectTo`, for the same reason the
        // re-authentication branch below is, and before that branch because the
        // server decides the same way: which account, then prove it (#1924).
        //
        // The request is still alive on the server; this is a question, not a
        // failure. Reading the generic redirect first would turn an answerable
        // chooser into a silent navigation carrying an error the user could have
        // cleared with one click.
        if (body?.error === 'OIDC_ACCOUNT_SELECTION_REQUIRED') {
          setClientName(
            typeof body.clientName === 'string' && body.clientName.length > 0
              ? body.clientName
              : undefined
          );
          // A SECOND refusal has to say something, for the same reason the
          // password form's does. Arriving here twice means the user pressed
          // "continue as this account" and the server minted for nobody — the
          // session changed underneath them, across tabs, between this page
          // rendering and the button being pressed. Without a message the page
          // repaints what looks like the identical dialog and reads as "the
          // button did nothing", when in fact the address in it has changed,
          // which is the one thing they need to notice.
          setSelectNotice(
            chooserRepeatedRef.current
              ? 'You are now signed in as a different account from the one you confirmed. Check the address below before continuing.'
              : null
          );
          chooserRepeatedRef.current = true;
          setPhase('select');
          return;
        }

        // Checked BEFORE `errorRedirectTo`, and the order is the point (#1904).
        // Every other failure here means "give up and go back to the relying
        // party"; this one means the opposite — the request is still alive on the
        // server precisely so the user can satisfy it. Reading the generic
        // redirect first would turn a recoverable password prompt into a silent
        // navigation carrying an error the user could have cleared in one step.
        if (body?.error === 'OIDC_REAUTH_REQUIRED') {
          setPassword('');
          // A SECOND refusal has to say something. The first one is expected and
          // the form explains itself; a repeat means the password was accepted
          // by Firebase and the server still would not have it, which without a
          // message renders as an identical empty form and reads as "nothing
          // happened". Rare — it needs the two clocks to disagree by more than
          // the freshness allowance — but the person is doing everything right
          // and would have no way to tell.
          setReauthError(
            challengedRef.current
              ? 'That password was accepted, but the application still needs a newer sign-in. Try once more, or cancel and go back.'
              : null
          );
          challengedRef.current = true;
          setPhase('reauth');
          return;
        }

        // A lapsed sign-in that the backend could still attribute to its
        // relying party comes back with that party's own error redirect, so the
        // user is returned to where they started rather than stranded on our
        // page with an instruction to find their own way back (#1831).
        if (body?.errorRedirectTo) {
          window.location.assign(body.errorRedirectTo);
          return;
        }

        // No OIDC_EMAIL_NOT_VERIFIED branch: nothing can return that code any
        // more (#1832), and keeping a dead handler would imply a refusal that
        // no longer exists.
        if (body?.error === 'OIDC_REQUEST_EXPIRED') {
          setErrorMessage(
            'This sign-in request has expired or was already used. Return to the application you came from and try again.'
          );
        } else {
          setErrorMessage(
            'Something went wrong while completing the sign-in. Return to the application you came from and try again.'
          );
        }
        setPhase('error');
      } catch {
        setErrorMessage(
          'Could not reach the sign-in service. Check your connection and try again.'
        );
        setPhase('error');
      }
    },
    [requestId, failSessionExpired]
  );

  useEffect(() => {
    // `!firebaseUser` and `!requestId` must both stay: without the former this
    // calls getIdToken on a signed-out browser and reports "session expired"
    // instead of routing to sign-in. `isLoading` is a WAIT, not a "nobody is
    // signed in" — acting on it would read a signed-in person as signed out.
    if (isLoading || !firebaseUser || !requestId) {
      return;
    }
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    void submitCompletion();
  }, [isLoading, firebaseUser, requestId, submitCompletion]);

  /**
   * Re-authenticate in place, then retry the exchange.
   *
   * `reauthenticateWithCredential` rather than signing out and back in, and
   * the difference is not stylistic:
   *
   * - Signing out would END the HeapsterID session to prove the person is
   *   present — the opposite of what the relying party asked for, and it would
   *   strand anyone who abandons the prompt in a signed-out state they never
   *   asked for.
   * - Firebase asserts the credential belongs to the SAME uid, so nobody can
   *   quietly re-authenticate as a different account half way through an
   *   authorization request that has already been bound to the first one.
   *
   * The email is displayed, never editable. The only question here is whether
   * the person at the keyboard is the account holder; offering an address field
   * would invite the account switch the uid assertion exists to refuse, and
   * turn a refusal into a confusing error instead of an impossible action.
   */
  const reauthenticate = async (event: FormEvent) => {
    event.preventDefault();
    if (actionPending) {
      return;
    }
    const current = auth.currentUser;
    if (!current?.email) {
      failSessionExpired();
      return;
    }

    setActionPending(true);
    setReauthError(null);
    try {
      await reauthenticateWithCredential(
        current,
        EmailAuthProvider.credential(current.email, password)
      );
    } catch (error) {
      setReauthError(reauthErrorMessage(error));
      setActionPending(false);
      return;
    }

    setPassword('');
    setPhase('working');
    await submitCompletion();
    setActionPending(false);
  };

  /**
   * "Continue as this account" — the one answer the server cannot work out for
   * itself (#1924).
   *
   * The subject sent is `firebaseUser.uid`, from React state, which is the
   * account this chooser actually RENDERED. Deliberately not
   * `auth.currentUser.uid`: `submitCompletion` mints against a token read live
   * from `auth.currentUser`, and Firebase persistence is shared across tabs on
   * one origin, so by the time this runs the two can genuinely differ.
   *
   * Sending the rendered subject is what gives the server's equality check
   * something to do. If the session changed underneath, the uids disagree, the
   * server refuses again, and the user is asked about the account they now hold
   * — instead of being silently handed over as it, having confirmed another.
   */
  const continueAsCurrentAccount = async () => {
    if (actionPending) {
      return;
    }
    const confirmedSub = firebaseUser?.uid;
    if (!confirmedSub) {
      failSessionExpired();
      return;
    }
    // Remembered so that a later Cancel can say WHICH question was refused.
    // Somebody who passes the chooser and then stops at the password form would
    // otherwise re-evaluate on the server as "selection still required", and
    // both refusals would look alike to the relying party.
    confirmedSubRef.current = confirmedSub;
    setActionPending(true);
    setPhase('working');
    await submitCompletion(confirmedSub);
    setActionPending(false);
  };

  /**
   * "Use a different account" — end the session here and let the existing
   * signed-out path do the rest (#1924).
   *
   * There is deliberately no navigation in this function. Signing out makes
   * `firebaseUser` null, this component re-renders, and the `!firebaseUser`
   * branch below sends the user to the sign-in form with `returnTo` pointing
   * back at this page — precisely the route a signed-out arrival already takes.
   * Reimplementing that here would be a second copy of a decision that has to
   * agree with the first.
   *
   * The stashed request is untouched: neither `/authorize/complete` nor
   * `/authorize/decline` was called, so it stays live for its full
   * `AUTH_REQUEST_TTL_SECONDS` and the relying party hears nothing yet.
   *
   * And it terminates. On the way back the server satisfies the selection from
   * `auth_time` alone — they authenticated after the request was stashed — so
   * nobody is asked to choose twice, whichever account they signed in as.
   *
   * Destructive in a way the other two answers are not: it ends the HeapsterID
   * session on this device. That is why Cancel sits beside it.
   */
  const switchAccount = async () => {
    if (actionPending) {
      return;
    }
    setActionPending(true);
    try {
      await signOut();
    } catch {
      setErrorMessage(
        'Could not sign you out. Return to the application you came from and try again.'
      );
      setPhase('error');
      setActionPending(false);
    }
    // No `setActionPending(false)` on success: the sign-out unmounts this
    // component by way of the redirect above, and setting state afterwards
    // would be a write to a component that is going away.
  };

  /**
   * The user refused. Tell the relying party, and let the SERVER build the URL
   * that says so — this page never assembles a redirect target, which is the
   * entire reason the request is stashed rather than passed along.
   *
   * The endpoint consumes the request. A refusal is a decision, not a pause,
   * and the request_id is still sitting in the address bar.
   */
  const decline = async () => {
    if (actionPending) {
      return;
    }
    setActionPending(true);
    try {
      // Force-refreshed, as `submitCompletion` does (#2140). The route is
      // strict, so the cached token of a revoked session would be a 401 and
      // the generic error below. A refused refresh makes the SDK sign out
      // instead, and the `!firebaseUser` branch sends them to sign-in with
      // this page as `returnTo`.
      const idToken = await auth.currentUser?.getIdToken(true);
      if (!idToken) {
        failSessionExpired();
        return;
      }
      const response = await fetch(
        `${appConfig.api.baseUrl}/authorize/decline`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          // Carries the confirmation if the chooser was answered, so the
          // relying party is told a password was refused rather than an
          // account. Absent when the user never reached that screen.
          body: JSON.stringify({
            request_id: requestId,
            ...(confirmedSubRef.current
              ? { confirmed_sub: confirmedSubRef.current }
              : {}),
          }),
        }
      );
      const body = (await response.json().catch(() => null)) as {
        redirectTo?: string;
        errorRedirectTo?: string;
      } | null;

      // Either key returns the user to the relying party. `redirectTo` carries
      // the decline itself; `errorRedirectTo` appears when the request had
      // already lapsed, which is still an answer and still better than leaving
      // them here.
      const target = response.ok ? body?.redirectTo : body?.errorRedirectTo;
      if (target) {
        window.location.assign(target);
        return;
      }

      setErrorMessage(
        'Something went wrong. Return to the application you came from and try again.'
      );
      setPhase('error');
    } catch {
      setErrorMessage(
        'Could not reach the sign-in service. Check your connection and try again.'
      );
      setPhase('error');
    }
    setActionPending(false);
  };

  if (!requestId) {
    return (
      <OidcErrorShell message="This sign-in link is incomplete. Return to the application you came from and try again." />
    );
  }

  // Scoped to the initial wait on purpose: a bare `if (isLoading)` here would
  // replace a half-typed password form with a spinner and remount it empty.
  //
  // Note what does NOT cause that, because the obvious guess is wrong and was
  // in this comment until it was checked. Re-authenticating does not flip
  // `isLoading`: `AuthContext` only sets it from `onAuthStateChanged`, and
  // Firebase's `notifyAuthListeners` pushes to that subscription only when the
  // **uid changes** — a same-user re-authentication notifies
  // `onIdTokenChanged` alone, which nothing here subscribes to. The scoping is
  // kept anyway; it costs a condition, and the day something else does toggle
  // `isLoading` mid-flow it is a lost password rather than a lost spinner.
  if (isLoading && phase === 'working') {
    return <LoadingSpinner fullScreen message="Checking authentication..." />;
  }

  // Route through the existing sign-in flow; returnTo brings the user back
  // here (including the request_id query string).
  //
  // `prompt=create` sends them to sign-UP instead. A relying party with no
  // sign-up form of its own has no other way to say "this person is here to
  // register", and landing every new user on a sign-in page to hunt for a link
  // is a step lost at exactly the moment people give up. Both destinations are
  // `PublicRoute`-wrapped and neither navigates on its own, so `returnTo` is
  // honoured identically from either.
  //
  // Note this only steers someone who is NOT already signed in. A user with a
  // live session is passed straight through to the exchange, even for
  // `prompt=create` — the registration spec says an OP SHOULD show registration
  // UI regardless, and this deliberately does not, because the alternative is
  // signing someone out of a working account or inviting them to create a
  // second one. Pinned by test so it stays a decision rather than an accident.
  //
  // `prompt=login` is NOT handled here, and must not be: somebody arriving with
  // no session is about to type their password on that very form, so treating
  // it here would ask them for the same password twice (#1904).
  if (!firebaseUser) {
    const wantsSignUp = searchParams
      .get('prompt')
      ?.split(' ')
      .includes('create');
    // `login_hint` travels on in history state rather than on the next URL, so
    // the address is not written into history a second time. It does NOT undo
    // the first: the 302 that brought us here already recorded it, before any
    // of this ran. `replace` keeps that entry off the back stack; it does not
    // scrub it.
    const loginHint = searchParams.get('login_hint') ?? undefined;
    return (
      <Navigate
        to={wantsSignUp ? '/signup' : '/'}
        replace
        state={{ returnTo: location, loginHint }}
      />
    );
  }

  // MUST stay below the `!firebaseUser` branch above (#1924). "Use a different
  // account" signs out, and for the render between the sign-out landing and the
  // redirect there is no session — a chooser placed above that branch would
  // paint itself with a blank address instead of navigating to sign-in.
  if (phase === 'select') {
    return (
      <OidcShell heading="Continue as this account?">
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          {clientName
            ? `${clientName} has asked you to confirm which HeapsterID account to use.`
            : 'The application you are signing in to has asked you to confirm which HeapsterID account to use.'}
        </p>

        {selectNotice && (
          <p role="alert" className="mt-4 text-sm text-red-600">
            {selectNotice}
          </p>
        )}

        <div className="mt-6 rounded-md bg-gray-50 px-4 py-3 text-left ring-1 ring-inset ring-gray-200">
          <p className="text-sm font-medium text-gray-700">Signed in as</p>
          <p className="mt-1 break-all text-sm text-gray-900">
            {firebaseUser.email ?? 'this account'}
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            onClick={() => void continueAsCurrentAccount()}
            disabled={actionPending}
            className="inline-flex justify-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-60 transition-colors duration-200"
          >
            {actionPending ? 'Continuing...' : 'Continue as this account'}
          </button>
          <button
            type="button"
            onClick={() => void switchAccount()}
            disabled={actionPending}
            className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-60 transition-colors duration-200"
          >
            Use a different account
          </button>
          {/* Said out loud, not left to the button label: this ends the
              HeapsterID session for the whole device, not merely this one
              sign-in, so somebody expecting to swap accounts "just for this
              application" needs telling.

              It does NOT reach other Heapster services, and this used to say
              that it did. `signOutUser` is `signOut(auth)` on our own Firebase
              project; heapster.live holds a separate session minted through
              the auth bridge, and nothing here revokes it. The partner pack
              states the same boundary under "Signing out" — the two have to
              agree, because a partner reads one and their user reads the
              other. */}
          <p className="text-xs text-gray-500">
            Using a different account signs you out of HeapsterID on this device
            — not just for this application.
          </p>
          {/* Beside the destructive option on purpose: "use a different
              account" ends the HeapsterID session on this device, so
              abandoning the question needs an exit that does not. */}
          <button
            type="button"
            onClick={() => void decline()}
            disabled={actionPending}
            className="inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-60 transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </OidcShell>
    );
  }

  if (phase === 'reauth') {
    return (
      <OidcShell heading="Confirm your password">
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          The application you are signing in to has asked us to check that it is
          really you before continuing.
        </p>
        <form onSubmit={e => void reauthenticate(e)} className="mt-6 text-left">
          {/* Present for password managers, which need a username field beside
              the password one to offer the right credential. Not editable: the
              account is already decided, and this request is bound to it. */}
          <label
            htmlFor="oidc-reauth-email"
            className="block text-sm font-medium text-gray-700"
          >
            Signed in as
          </label>
          <input
            id="oidc-reauth-email"
            name="username"
            type="email"
            autoComplete="username"
            readOnly
            value={firebaseUser.email ?? ''}
            className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 text-gray-600 shadow-sm sm:text-sm"
          />

          <label
            htmlFor="oidc-reauth-password"
            className="mt-4 block text-sm font-medium text-gray-700"
          >
            Password
          </label>
          <input
            id="oidc-reauth-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            autoFocus
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={actionPending}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
          />

          {reauthError && (
            <p role="alert" className="mt-3 text-sm text-red-600">
              {reauthError}
            </p>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={actionPending}
              className="inline-flex justify-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-60 transition-colors duration-200"
            >
              {actionPending ? 'Confirming...' : 'Confirm'}
            </button>
            <button
              type="button"
              onClick={() => void decline()}
              disabled={actionPending}
              className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-60 transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </form>
      </OidcShell>
    );
  }

  if (phase === 'error') {
    return (
      <OidcErrorShell
        message={errorMessage ?? 'Sign-in failed.'}
        // Carries returnTo, so signing in again resumes this same request
        // rather than dropping the user on the dashboard.
        signInTo={showSignIn ? location : undefined}
        // And the hint, so the form they are sent back to still prefills. The
        // person taking this link has already had one sign-in go wrong; making
        // them retype the address is where a migrating user types a different
        // one and orphans their subscription.
        loginHint={searchParams.get('login_hint') ?? undefined}
      />
    );
  }

  return (
    <LoadingSpinner fullScreen message="Signing you in to the application..." />
  );
}

function OidcShell({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
            {heading}
          </h1>
          {children}
        </div>
      </div>
    </div>
  );
}

function OidcErrorShell({
  message,
  signInTo,
  loginHint,
}: {
  message: string;
  /** When set, offer a sign-in link carrying this location as returnTo. */
  signInTo?: ReturnType<typeof useLocation>;
  /** The relying party's address hint, so the sign-in form still prefills. */
  loginHint?: string;
}) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
            Sign-In Could Not Be Completed
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">{message}</p>
          {signInTo && (
            <p className="mt-4 text-sm">
              <Link
                to="/"
                replace
                state={{ returnTo: signInTo, loginHint }}
                className="font-medium text-sky-600 hover:text-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded transition-colors duration-200"
              >
                Sign in again
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
